/**
 * App Component
 * AIDLC Administrative Backend
 *
 * Root component with React Router v6 configuration.
 * All routes are wrapped in MainLayout which provides Sidebar + Header.
 *
 * Authentication is handled by MsalProvider in main.tsx.
 * This component handles routing only.
 *
 * Route structure -- original modules + new feature placeholders:
 *   /dashboard       -> Agent Dashboard (DASHBOARD_PATH; a fresh load of / is the public landing page, PORTAL-RELOAD-1)
 *   /                -> redirect to /dashboard (in-app links and the sign-in return)
 *   /agents/:id      -> Agent Detail
 *   /hiring          -> Hiring Pipeline
 *   /governance      -> Governance & Compliance
 *   /control         -> Control Room
 *   /lifecycle       -> AIDLC Lifecycle
 *   /chat            -> Chat Interface
 *   /audit           -> Audit & History
 *   /templates       -> Standard Operating Procedures (SOPs)
 *   /settings        -> Settings
 *   /backlog         -> Backlog
 *   /tokens          -> Token Management
 *   /projects        -> Project Management / Sprint Board
 *   /projects/overview -> Projects Overview
 *
 * New feature routes (placeholder pages):
 *   /proposals       -> Budget Proposals
 *   /roadmap         -> Token Roadmap
 *   /milestones      -> Milestone Tracker
 *   /scheduler       -> Agent Scheduler
 *   /spawn-planner   -> Spawn Planner
 *   /assessments     -> Assessment Reports
 *   /burn-monitor    -> Budget Burn Monitor
 *   /escalations     -> Escalation Queue
 *   /dod-pipeline    -> DoD Pipeline
 *   /estimation      -> Token Estimation
 *   /training        -> Training Platform
 *   /deployment      -> Deployment Pipeline + Azure Dashboard
 *   /deployment-guide -> Deployment Guide (interactive DEPLOYMENT.md)
 *   /docs            -> Documentation Hub
 *   /docs/email      -> Email Management Guide
 *   /docs/user-guide -> User Guide
 *   /docs/briefs     -> Executive Briefs (BRIEFS-1)
 *   /admin           -> redirect to /dashboard (old bookmark; /admin was in the Deployment Guide)
 *   *                -> Not Found page (inside the layout)
 */

import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { DASHBOARD_PATH } from './routes';
import { MainLayout } from './components/layout/MainLayout';
import { subscribeToPush } from './services/pushSubscription';
import { ErrorBoundary } from './components/shared/ErrorBoundary';
import { NotFoundPage } from './components/shared/NotFoundPage';

// ─── Existing page components ───────────────────────────────────
import { AgentDashboard } from './components/agents/AgentDashboard';
import { AgentProfilePage } from './components/agents/AgentProfilePage';
import { GovernancePage } from './components/governance/GovernancePage';
import { ControlRoomPage } from './components/control/ControlRoomPage';
import { LifecyclePage } from './components/aidlc/LifecyclePage';
import { ChatPage } from './components/chat/ChatPage';
import { AuditPage } from './components/audit/AuditPage';
import { TemplatesPage } from './components/templates/TemplatesPage';
import { SettingsPage } from './components/shared/SettingsPage';
import { HiringPage } from './components/hiring/HiringPage';
import { BacklogPage } from './components/backlog/BacklogPage';
import { TokenManagementPage } from './components/tokens/TokenManagementPage';
import { ProjectManagementPage } from './components/projects/ProjectManagementPage';
import { ProjectsOverviewPage } from './components/projects/overview/ProjectsOverviewPage';

// ─── New feature placeholder pages ──────────────────────────────
import { BudgetProposalsPage } from './components/proposals/BudgetProposalsPage';
import { TokenRoadmapPage } from './components/roadmap/TokenRoadmapPage';
import { MilestoneTrackerPage } from './components/milestones/MilestoneTrackerPage';
import { AgentSchedulerPage } from './components/scheduler/AgentSchedulerPage';
import { SpawnPlannerPage } from './components/spawn/SpawnPlannerPage';
import { AssessmentReportsPage } from './components/assessments/AssessmentReportsPage';
import { BudgetBurnMonitorPage } from './components/burn/BudgetBurnMonitorPage';
import { EscalationQueuePage } from './components/escalations/EscalationQueuePage';
import { DodPipelinePage } from './components/dod/DodPipelinePage';
import { TokenEstimationPage } from './components/estimation/TokenEstimationPage';
import { ReviewReportsPage } from './components/reviews/ReviewReportsPage';
import { RecurringTasksPage } from './components/recurring/RecurringTasksPage';
import { FeatureTrackerPage } from './components/features/FeatureTrackerPage';
import { TrainingPage } from './components/training/TrainingPage';
import { DeploymentPipelinePage } from './components/deployment/DeploymentPipelinePage';
import { DeploymentGuidePage } from './components/shared/DeploymentGuidePage';
import { EmailManagementPage } from './components/email/EmailManagementPage';
import { EmailTemplatePage } from './components/email/EmailTemplatePage';
import { EmailAutomationPage } from './components/email/EmailAutomationPage';
import { DocumentationHubPage } from './components/docs/DocumentationHubPage';
import { EmailDocumentationPage } from './components/docs/EmailDocumentationPage';
import { UserGuidePage } from './components/docs/UserGuidePage';
import { ExecutiveBriefsPage } from './components/docs/ExecutiveBriefsPage';
import { SecretsInventoryPage } from './components/secrets/SecretsInventoryPage';
import { QaDashboardPage } from './components/qa/QaDashboardPage';
import { DispatcherControlPage } from './components/dispatcher/DispatcherControlPage';

export function App() {
  // Subscribe this browser to push after sign-in (App only renders when authenticated); quiet when push is off (PUSH-SUB-1)
  useEffect(() => {
    void subscribeToPush();
  }, []);

  return (
    <ErrorBoundary scope="app">
      <BrowserRouter>
        <Routes>
          <Route element={<MainLayout />}>
            {/* ─── Existing routes (preserved) ─────────────────── */}
            <Route index element={<Navigate to={DASHBOARD_PATH} replace />} />
            <Route path="dashboard" element={<AgentDashboard />} />
            <Route path="agents/:id" element={<AgentProfilePage />} />
            <Route path="hiring" element={<HiringPage />} />
            <Route path="governance" element={<GovernancePage />} />
            <Route path="control" element={<ControlRoomPage />} />
            <Route path="lifecycle" element={<LifecyclePage />} />
            <Route path="chat" element={<ChatPage />} />
            <Route path="audit" element={<AuditPage />} />
            <Route path="templates" element={<TemplatesPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="backlog" element={<BacklogPage />} />
            <Route path="tokens" element={<TokenManagementPage />} />
            <Route path="projects" element={<ProjectManagementPage />} />
            <Route path="projects/overview" element={<ProjectsOverviewPage />} />

            {/* ─── New feature routes (placeholders) ───────────── */}
            <Route path="proposals" element={<BudgetProposalsPage />} />
            <Route path="roadmap" element={<TokenRoadmapPage />} />
            <Route path="milestones" element={<MilestoneTrackerPage />} />
            <Route path="scheduler" element={<AgentSchedulerPage />} />
            <Route path="spawn-planner" element={<SpawnPlannerPage />} />
            <Route path="assessments" element={<AssessmentReportsPage />} />
            <Route path="burn-monitor" element={<BudgetBurnMonitorPage />} />
            <Route path="escalations" element={<EscalationQueuePage />} />
            <Route path="dod-pipeline" element={<DodPipelinePage />} />
            <Route path="estimation" element={<TokenEstimationPage />} />
            <Route path="review-reports" element={<ReviewReportsPage />} />
            <Route path="recurring-tasks" element={<RecurringTasksPage />} />
            <Route path="features" element={<FeatureTrackerPage />} />
            <Route path="training" element={<TrainingPage />} />
            <Route path="deployment" element={<DeploymentPipelinePage />} />
            <Route path="deployment-guide" element={<DeploymentGuidePage />} />
            <Route path="email" element={<EmailManagementPage />} />
            <Route path="email/templates" element={<EmailTemplatePage />} />
            <Route path="email/automation" element={<EmailAutomationPage />} />
            <Route path="secrets" element={<SecretsInventoryPage />} />
            <Route path="qa-dashboard" element={<QaDashboardPage />} />
            <Route path="dispatcher" element={<DispatcherControlPage />} />
            <Route path="docs" element={<DocumentationHubPage />} />
            <Route path="docs/email" element={<EmailDocumentationPage />} />
            <Route path="docs/user-guide" element={<UserGuidePage />} />
            <Route path="docs/briefs" element={<ExecutiveBriefsPage />} />

            {/* ─── Old bookmarks and unknown URLs ──────────────── */}
            <Route path="admin" element={<Navigate to={DASHBOARD_PATH} replace />} />
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
